import { useCallback, useRef } from 'react';
import { useSessionStorageState } from '../../../util/useSessionStorageState';
import { TableFilter } from './metadata/propertyConfig';
import { DateRangeValue } from './date-range-filter/DateRangeFilter';

export type EntityFilterOption = { value: string; label: string };

/** The filter half of a listing's persisted state. Page, page size and sort
 *  are a sibling key with their own owner — see listingPaging.ts (DV-3455). */
export type ListingFilters = {
  filter: TableFilter;
  activeStatus: { value: string; label: string } | null | undefined;
  orderDateRange: DateRangeValue | null;
  // Order-status filter chips; concrete shape lives at MultiSelect's `Option`
  // contract but importing it here would cross feature/UI layering. Banked
  // Pass 14 for unification with MultiSelect's Option type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  orderStatusValues: any[];
  selectedEntityFilters: EntityFilterOption[];
  moreFilters: string;
  filtersCount: number;
};

export const FILTER_BASE: TableFilter = {
  address: null,
  fullAddress: null,
  status: null,
  orderStatus: null,
  orderDateFrom: null,
  orderDateTo: null,
  entityUUIDs: null
};

export const DEFAULT_LISTING_FILTERS: ListingFilters = {
  filter: { ...FILTER_BASE },
  activeStatus: undefined,
  orderDateRange: null,
  orderStatusValues: [],
  selectedEntityFilters: [],
  moreFilters: '',
  filtersCount: 0
};

export function useListingFilters(
  storageKey: string,
  initialValue: ListingFilters
) {
  const [filters, setFilters] = useSessionStorageState<ListingFilters>(
    storageKey,
    initialValue
  );

  // Capture initialValue at mount; reset always restores the value the
  // hook saw on first render, mirroring useState's lazy-initializer semantics.
  const initialRef = useRef(initialValue);

  const reset = useCallback(() => {
    setFilters(initialRef.current);
  }, [setFilters]);

  return { filters, setFilters, reset };
}
